import { Component, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * Full-screen blocking loader for the slow cross-border writes — raising an export request
 * finalises the event selection and then writes to the chain before the call returns, which runs
 * to several seconds.
 *
 * <p>The step captions are <strong>time-based, not server-driven</strong>: the backend does this
 * work inside one request and reports no progress events, so the captions describe the sequence
 * that is genuinely running rather than confirming which part has finished. They advance once and
 * hold on the last one, so nothing ever claims to be further along than it can know.
 */
@Component({
  selector: 'app-globe-loader',
  imports: [CommonModule],
  templateUrl: './globe-loader.html',
  styleUrl: './globe-loader.scss',
})
export class GlobeLoader implements OnInit, OnDestroy {
  @Input() title = 'Working…';

  /** Captions shown in order. The last one holds until the component is destroyed. */
  @Input() steps: string[] = [];

  /** How long each caption shows before the next, in ms. */
  @Input() stepDurationMs = 2400;

  stepIndex = signal(0);

  private timer?: ReturnType<typeof setInterval>;

  get caption(): string {
    return this.steps[this.stepIndex()] ?? '';
  }

  ngOnInit(): void {
    if (this.steps.length <= 1) {
      return;
    }

    this.timer = setInterval(() => {
      const next = this.stepIndex() + 1;
      if (next >= this.steps.length - 1) {
        this.stepIndex.set(this.steps.length - 1);
        this.stop();
        return;
      }
      this.stepIndex.set(next);
    }, this.stepDurationMs);
  }

  ngOnDestroy(): void {
    this.stop();
  }

  private stop(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = undefined;
    }
  }
}
